import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { statsService } from '../services/api/statsService';
import { useAuthStore } from '../stores/authStore';
import { useAuth } from '../hooks/useAuth';
import { VictoryCelebration, VictoryHeader } from '../components/common';

// Local types to avoid import issues with shared package
type HuntWideStats = {
  totalDurationMs: number;
  totalPins: number;
  totalItems: number;
  totalChallenges: number;
  hunterCount: number;
  hiderCount: number;
  totalHunterWalkDistanceM: number;
  totalHiderSetupWalkDistanceM: number;
};

type HunterStatistics = {
  userId: string;
  displayName: string;
  itemsFound: number;
  challengesEncounteredAsCaptain: number;
  challengesCompletedSuccessfully: number;
  timesFirstToReachPin: number;
  walkDistanceM: number;
  timesCaughtCheating: number;
  incorrectChallengeResponses: number;
  challengesForfeit: number;
  title: string;
};

type HiderStatistics = {
  userId: string;
  displayName: string;
  pinsCreated: number;
  itemsPlaced: number;
  challengesCreated: number;
  setupWalkDistanceM: number;
};

type HiderGroupStatistics = {
  hiderDisplayNames: string[];
  pinsSetup: number;
  averageHunterWalkDistanceM: number;
  walkDistanceDifferential: number;
  walkDistanceIsGood: boolean;
  totalIncorrectChallengeResponses: number;
  title: string;
};

type AchievementEntry = {
  category: 'positive' | 'negative';
  statName: string;
  statLabel: string;
  userId: string;
  displayName: string;
  value: number;
  unit: string;
  title: string;
};

type HuntStatsResponse = {
  huntStats: HuntWideStats;
  achievements: AchievementEntry[];
  myStats: HunterStatistics | HiderStatistics | null;
  hiderGroupStats: HiderGroupStatistics;
  expiresAt: string;
};

/**
 * Format duration in milliseconds to mm:ss
 */
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format distance in meters
 */
function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`;
  }
  return `${Math.round(meters)} m`;
}

/**
 * Calculate time until expiry
 */
function getTimeUntilExpiry(expiresAt: string): string {
  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;

  if (remaining <= 0) {
    return 'Expired';
  }

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/**
 * Statistics page showing hunt results
 */
export function StatsPage() {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { isGuest, signOut } = useAuth();

  const [stats, setStats] = useState<HuntStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiryDisplay, setExpiryDisplay] = useState('');
  const [showCelebration, setShowCelebration] = useState(true);

  // Load stats
  const loadStats = useCallback(async () => {
    if (!huntId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await statsService.getHuntStats(huntId);
      setStats(data);
      setExpiryDisplay(getTimeUntilExpiry(data.expiresAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  }, [huntId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Update expiry display periodically
  useEffect(() => {
    if (!stats?.expiresAt) return;

    const interval = setInterval(() => {
      setExpiryDisplay(getTimeUntilExpiry(stats.expiresAt));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [stats?.expiresAt]);

  const handleStartNew = async () => {
    if (isGuest) {
      await signOut();
      navigate('/');
    } else {
      navigate('/welcome');
    }
  };

  const handleDismissCelebration = () => {
    setShowCelebration(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-spin text-4xl">⟳</div>
      </div>
    );
  }

  // Error state
  if (error || !stats) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center p-8">
        <div className="text-red-500 text-6xl mb-4">📊</div>
        <h1 className="text-white text-xl font-bold mb-2">Statistics Unavailable</h1>
        <p className="text-gray-400 text-center mb-6">
          {error || 'Statistics have expired'}
        </p>
        <button
          onClick={handleStartNew}
          className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
        >
          Start New Hunt
        </button>
      </div>
    );
  }

  // Celebration screen (shown first before stats)
  if (showCelebration) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center relative overflow-hidden">
        <VictoryCelebration />
        <div className="relative z-10">
          <VictoryHeader subtitle="You found all the pins!" />
          <button
            onClick={handleDismissCelebration}
            className="mt-12 mx-auto block px-8 py-4 bg-red-500 text-white text-lg font-bold rounded-xl hover:bg-red-600 active:scale-95 transition-all animate-pulse"
          >
            See Results
          </button>
        </div>
      </div>
    );
  }

  const { huntStats, achievements, myStats, hiderGroupStats } = stats;

  // Separate positive and negative achievements
  const positiveAchievements = achievements.filter(a => a.category === 'positive');
  const negativeAchievements = achievements.filter(a => a.category === 'negative');

  return (
    <div className="h-full bg-[#0A0A0F] flex flex-col overflow-hidden">
      {/* Header with expiry */}
      <header className="px-4 py-3 border-b border-gray-800 flex justify-end">
        <span className="text-gray-400 text-sm">
          Stats expire in {expiryDisplay}
        </span>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Title */}
        <div className="py-8 relative z-10">
          <VictoryHeader />
        </div>

        {/* Hunt Summary */}
        <section className="px-4 pb-6">
          <h2 className="text-gray-400 text-sm font-semibold text-center mb-4 uppercase tracking-wide">
            Hunt Summary
          </h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-800/50 rounded-lg py-4">
              <div className="text-white text-2xl font-bold">
                {formatDuration(huntStats.totalDurationMs)}
              </div>
              <div className="text-gray-400 text-sm">Duration</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg py-4">
              <div className="text-white text-2xl font-bold">{huntStats.totalPins}</div>
              <div className="text-gray-400 text-sm">Pins</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg py-4">
              <div className="text-white text-2xl font-bold">{huntStats.totalItems}</div>
              <div className="text-gray-400 text-sm">Items</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center mt-4">
            <div className="bg-gray-800/50 rounded-lg py-4">
              <div className="text-white text-2xl font-bold">
                {huntStats.hunterCount}
              </div>
              <div className="text-gray-400 text-sm">Hunters</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg py-4">
              <div className="text-white text-2xl font-bold">
                {formatDistance(huntStats.totalHunterWalkDistanceM)}
              </div>
              <div className="text-gray-400 text-sm">Total Walked</div>
            </div>
          </div>
        </section>

        {/* Hider Group Stats */}
        <section className="px-4 pb-6">
          <h2 className="text-gray-400 text-sm font-semibold text-center mb-4 uppercase tracking-wide">
            Hider Performance
          </h2>
          <div className="bg-gray-800/50 rounded-xl p-4">
            <div className="text-center mb-3">
              <div className="text-red-400 font-semibold text-lg">{hiderGroupStats.title}</div>
              <div className="text-gray-400 text-sm">
                {hiderGroupStats.hiderDisplayNames.join(', ')}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-white text-xl font-bold">{hiderGroupStats.pinsSetup}</div>
                <div className="text-gray-400 text-sm">Pins Setup</div>
              </div>
              <div>
                <div className={`text-xl font-bold ${hiderGroupStats.walkDistanceIsGood ? 'text-green-400' : 'text-red-400'}`}>
                  {hiderGroupStats.walkDistanceDifferential > 0 ? '+' : ''}
                  {formatDistance(hiderGroupStats.walkDistanceDifferential)}
                </div>
                <div className="text-gray-400 text-sm">Walk Differential</div>
              </div>
            </div>
          </div>
        </section>

        {/* Positive Achievements */}
        {positiveAchievements.length > 0 && (
          <section className="px-4 pb-6">
            <h2 className="text-gray-400 text-sm font-semibold text-center mb-4 uppercase tracking-wide">
              🏆 Achievements
            </h2>
            <div className="space-y-3">
              {positiveAchievements.map((achievement) => (
                <AchievementRow
                  key={achievement.statName}
                  achievement={achievement}
                  isCurrentUser={achievement.userId === user?.uid}
                />
              ))}
            </div>
          </section>
        )}

        {/* Negative Achievements (Shame Hall) */}
        {negativeAchievements.length > 0 && (
          <section className="px-4 pb-6">
            <h2 className="text-gray-400 text-sm font-semibold text-center mb-4 uppercase tracking-wide">
              😅 Hall of Shame
            </h2>
            <div className="space-y-3">
              {negativeAchievements.map((achievement) => (
                <AchievementRow
                  key={achievement.statName}
                  achievement={achievement}
                  isCurrentUser={achievement.userId === user?.uid}
                  isNegative
                />
              ))}
            </div>
          </section>
        )}

        {/* Your Stats */}
        {myStats && (
          <section className="px-4 pb-6">
            <h2 className="text-gray-400 text-sm font-semibold text-center mb-4 uppercase tracking-wide">
              Your Stats
            </h2>
            <MyStatsCard stats={myStats} />
          </section>
        )}
      </div>

      {/* Footer action */}
      <footer className="p-4 border-t border-gray-800">
        <button
          onClick={handleStartNew}
          className="w-full py-4 bg-red-500 text-white text-lg font-semibold rounded-lg hover:bg-red-600 active:scale-98 transition-all"
        >
          START NEW HUNT
        </button>
      </footer>
    </div>
  );
}

interface AchievementRowProps {
  achievement: AchievementEntry;
  isCurrentUser: boolean;
  isNegative?: boolean;
}

function AchievementRow({ achievement, isCurrentUser, isNegative }: AchievementRowProps) {
  return (
    <div
      className={`
        flex items-center p-4 rounded-xl bg-gray-800/50
        ${isCurrentUser ? 'ring-2 ring-red-500/50' : ''}
      `}
    >
      {/* Icon */}
      <div className="w-10 text-2xl text-center">
        {isNegative ? '😬' : '🏆'}
      </div>

      {/* Name and title */}
      <div className="flex-1 ml-3">
        <div className="text-white font-medium">
          {achievement.displayName}
          {isCurrentUser && (
            <span className="text-gray-400 text-sm ml-1">(you)</span>
          )}
        </div>
        <div className={`text-sm ${isNegative ? 'text-orange-400' : 'text-red-400'}`}>
          {achievement.title}
        </div>
      </div>

      {/* Value */}
      <div className="text-right">
        <div className="text-white font-bold">{achievement.value} {achievement.unit}</div>
        <div className="text-gray-400 text-sm">{achievement.statLabel}</div>
      </div>
    </div>
  );
}

interface MyStatsCardProps {
  stats: HunterStatistics | HiderStatistics;
}

function isHunterStats(stats: HunterStatistics | HiderStatistics): stats is HunterStatistics {
  return 'itemsFound' in stats;
}

function MyStatsCard({ stats }: MyStatsCardProps) {
  if (isHunterStats(stats)) {
    return (
      <div className="bg-gray-800/50 rounded-xl p-4">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-white text-2xl font-bold">{stats.itemsFound}</div>
            <div className="text-gray-400 text-sm">Items Found</div>
          </div>
          <div>
            <div className="text-white text-2xl font-bold">{stats.challengesCompletedSuccessfully}</div>
            <div className="text-gray-400 text-sm">Challenges Solved</div>
          </div>
          <div>
            <div className="text-white text-2xl font-bold">{stats.timesFirstToReachPin}</div>
            <div className="text-gray-400 text-sm">First to Pin</div>
          </div>
          <div>
            <div className="text-white text-2xl font-bold">{formatDistance(stats.walkDistanceM)}</div>
            <div className="text-gray-400 text-sm">Distance Walked</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-700 text-center">
          <div className="text-red-400 font-semibold">{stats.title}</div>
        </div>
      </div>
    );
  }
  
  // Hider stats
  return (
    <div className="bg-gray-800/50 rounded-xl p-4">
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-white text-2xl font-bold">{stats.pinsCreated}</div>
          <div className="text-gray-400 text-sm">Pins Created</div>
        </div>
        <div>
          <div className="text-white text-2xl font-bold">{stats.itemsPlaced}</div>
          <div className="text-gray-400 text-sm">Items Placed</div>
        </div>
        <div>
          <div className="text-white text-2xl font-bold">{stats.challengesCreated}</div>
          <div className="text-gray-400 text-sm">Challenges</div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-700 text-center">
        <div className="text-gray-400 text-sm">Setup Walk: {formatDistance(stats.setupWalkDistanceM)}</div>
      </div>
    </div>
  );
}

export default StatsPage;
